import { useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api, qs, useAction, useApi } from '../lib/api';
import { date, money, titleCase } from '../lib/format';
import { AccountSelect, useAccounts, useBankAccounts, useSubsidiaries } from '../lib/lookups';
import { useSession } from '../lib/session';
import type { AutoMatchResult, BankAccount, BankTransaction, ImportResult, MatchSuggestion, ReviewItem, Rule } from '../lib/types';
import { Icons } from '../components/icons';
import { ArchiveButton, ArchivedBadge, ShowArchived } from '../components/archive';
import { Badge, Button, Card, cx, Empty, ErrorNote, Field, Input, Modal, PageHeader, Query, Select, StatusBadge, Table, Tabs, Td, Textarea, Th, Toggle, Tr } from '../components/ui';

type Tab = 'review' | 'transactions' | 'accounts' | 'rules';

const TABS: Tab[] = ['review', 'transactions', 'accounts', 'rules'];

export function CashPage() {
  // The tab lives in the URL, so other pages can link straight to the bank accounts (`?tab=accounts&add=1`).
  const [params, setParams] = useSearchParams();
  const tab = TABS.find((t) => t === params.get('tab')) ?? 'review';
  const setTab = (t: Tab) => setParams(t === 'review' ? {} : { tab: t }, { replace: true });
  const unmatched = useApi<BankTransaction[]>('/api/bank-transactions?status=UNMATCHED&limit=500');
  return (
    <>
      <PageHeader
        title="Bank reconciliation"
        description="Every suggestion shows exactly why it scored the way it did. Autopilot only confirms matches that clear the threshold with a clear lead over the runner-up."
      />
      <Tabs
        className="mb-4"
        value={tab}
        onChange={setTab}
        items={[
          { key: 'review', label: 'To review', count: unmatched.data?.length },
          { key: 'transactions', label: 'All transactions' },
          { key: 'accounts', label: 'Bank accounts' },
          { key: 'rules', label: 'Rules' },
        ]}
      />
      {tab === 'review' && <ReviewTab />}
      {tab === 'transactions' && <TransactionsTab />}
      {tab === 'accounts' && <AccountsTab />}
      {tab === 'rules' && <RulesTab />}
    </>
  );
}

// --- Review -------------------------------------------------------------------------------------------------------

function ReviewTab() {
  const { org, can } = useSession();
  const banks = useBankAccounts();
  const [bankId, setBankId] = useState('');
  const q = useApi<ReviewItem[]>(`/api/bank-transactions/review${qs({ bankAccountId: bankId })}`);
  const [threshold, setThreshold] = useState(org.settings.autoMatchThreshold ?? 90);
  const [preview, setPreview] = useState<AutoMatchResult | null>(null);
  const dry = useAction((t: number) => api<AutoMatchResult>('/api/cash/autopilot', { body: { threshold: t, dryRun: true } }), { silent: false });
  const runAuto = useAction((t: number) => api<AutoMatchResult>('/api/cash/autopilot', { body: { threshold: t } }), { success: (r) => `Autopilot matched ${r.matched} of ${r.reviewed} transactions` });

  return (
    <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_320px]">
      <div>
        <div className="mb-3 flex flex-wrap gap-1.5">
          <Chip active={!bankId} onClick={() => setBankId('')}>
            All accounts
          </Chip>
          {banks.list.map((b) => (
            <Chip key={b.id} active={bankId === b.id} onClick={() => setBankId(b.id)}>
              {b.name} <span className="text-faint">{b.currency}</span>
            </Chip>
          ))}
        </div>
        <Query q={q}>
          {(rows) =>
            rows.length === 0 ? (
              <Card>
                <Empty title="Fully reconciled" icon={<Icons.CheckCircle size={28} />}>
                  Every bank transaction is matched, categorized or excluded.
                </Empty>
              </Card>
            ) : (
              <div className="space-y-2.5">
                {rows.map((r) => (
                  <ReviewRow key={r.transaction.id} item={r} bank={banks.byId.get(r.transaction.bankAccountId)} />
                ))}
              </div>
            )
          }
        </Query>
      </div>
      {can('ACCOUNTANT') && (
        <div className="space-y-5">
          <Card title={<span className="flex items-center gap-2"><Icons.Bolt size={15} /> Autopilot</span>}>
            <p className="text-[12.5px] text-muted">Confirms the top suggestion when it scores at least the threshold and beats the runner-up by 10 points or more.</p>
            <div className="mt-3">
              <div className="mb-1 flex justify-between text-[12px]">
                <span className="text-muted">Confidence threshold</span>
                <span className="num font-semibold">{threshold}</span>
              </div>
              <input
                type="range"
                min={60}
                max={100}
                value={threshold}
                onChange={(e) => {
                  setThreshold(Number(e.target.value));
                  setPreview(null);
                }}
                className="w-full accent-[var(--color-brand)]"
              />
            </div>
            {preview && (
              <div className="mt-3 rounded-md bg-paper p-2.5 text-[12.5px]">
                <div className="font-medium">
                  Would match {preview.decisions.length} of {preview.reviewed}
                </div>
                <ul className="mt-1.5 max-h-48 space-y-1 overflow-y-auto scrollbar-thin">
                  {preview.decisions.map((d) => (
                    <li key={d.transactionId} className="flex justify-between gap-2 text-muted">
                      <span className="truncate">{d.suggestion.label}</span>
                      <span className="num shrink-0 text-good">{d.suggestion.score}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="mt-3 flex gap-2">
              <Button className="flex-1" loading={dry.isPending} onClick={() => dry.mutate(threshold, { onSuccess: setPreview })}>
                Preview
              </Button>
              <Button variant="primary" className="flex-1" loading={runAuto.isPending} onClick={() => runAuto.mutate(threshold, { onSuccess: () => setPreview(null) })}>
                Run now
              </Button>
            </div>
          </Card>
          <Card title="How scoring works">
            <ul className="space-y-1.5 text-[12px] text-muted">
              <li>
                <b className="text-ink-2">Amount</b> exact 45, within 1% 30, partial 12
              </li>
              <li>
                <b className="text-ink-2">Reference</b> document number in the description 35
              </li>
              <li>
                <b className="text-ink-2">Name</b> counterparty similarity up to 25
              </li>
              <li>
                <b className="text-ink-2">Date</b> closeness to due or issue date up to 15
              </li>
              <li>
                <b className="text-ink-2">Rule</b> learned rules 70 and up, rising with hits
              </li>
            </ul>
          </Card>
        </div>
      )}
    </div>
  );
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} className={cx('inline-flex h-7 items-center gap-1.5 rounded-full border px-3 text-[12.5px] transition-colors', active ? 'border-brand bg-brand-soft text-brand' : 'border-line bg-surface text-ink-2 hover:border-line-strong')}>
      {children}
    </button>
  );
}

function ReviewRow({ item, bank }: { item: ReviewItem; bank?: BankAccount }) {
  const { can } = useSession();
  const t = item.transaction;
  const cur = bank?.currency ?? 'USD';
  const [open, setOpen] = useState(item.suggestions.length > 0 && item.suggestions[0]!.score >= 60);
  const [categorize, setCategorize] = useState(false);
  const match = useAction((s: MatchSuggestion) => api(`/api/bank-transactions/${t.id}/match`, { body: { targetType: s.targetType, targetId: s.targetId, score: s.score, explanation: s.explanation } }), { success: 'Matched and posted' });
  const exclude = useAction(() => api(`/api/bank-transactions/${t.id}/exclude`, { body: { reason: 'Excluded during review' } }), { success: 'Excluded from reconciliation' });
  const top = item.suggestions[0];

  return (
    <section className="glass rounded-2xl">
      <div className="flex items-center gap-3 px-4 py-3">
        <button onClick={() => setOpen((o) => !o)} className={cx('text-faint transition-transform', open && 'rotate-90')} aria-label="Toggle suggestions">
          <Icons.Chevron size={15} />
        </button>
        <div className="w-24 shrink-0 text-[12.5px] text-muted">{date(t.date)}</div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-medium">{t.counterparty || t.description}</div>
          <div className="truncate text-[12px] text-muted">
            {t.description} · {bank?.name}
          </div>
        </div>
        {top ? <ScorePill score={top.score} /> : <Badge>No match found</Badge>}
        <div className={cx('num w-32 text-right text-[14px] font-semibold', t.amount > 0 ? 'text-good' : 'text-ink')}>{money(t.amount, cur, { sign: true })}</div>
        {can('ACCOUNTANT') && (
          <div className="flex gap-1.5">
            {top && top.score >= 40 && (
              <Button size="sm" variant="primary" loading={match.isPending && match.variables === top} onClick={() => match.mutate(top)}>
                Match
              </Button>
            )}
            <Button size="sm" onClick={() => setCategorize(true)}>
              Categorize
            </Button>
          </div>
        )}
      </div>
      {open && (
        <div className="border-t border-line bg-paper/50 px-4 py-3">
          {item.suggestions.length === 0 ? (
            <p className="text-[12.5px] text-muted">Nothing in AR, AP, transfers or rules lines up with this transaction. Categorize it to an account; tick “learn” to create a rule for next time.</p>
          ) : (
            <div className="grid gap-2.5 lg:grid-cols-2">
              {item.suggestions.map((s, i) => (
                <div key={`${s.targetType}:${s.targetId}`} className={cx('rounded-md border bg-surface p-3', i === 0 ? 'border-brand-line' : 'border-line')}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge tone={s.targetType === 'INVOICE' ? 'info' : s.targetType === 'BILL' ? 'warn' : s.targetType === 'RULE' ? 'brand' : 'neutral'}>{titleCase(s.targetType)}</Badge>
                        <span className="truncate text-[13px] font-medium">{s.label}</span>
                      </div>
                      <div className="num mt-0.5 text-[12px] text-muted">Applies {money(s.amount, cur)}</div>
                    </div>
                    <ScorePill score={s.score} />
                  </div>
                  <ul className="mt-2.5 space-y-1">
                    {s.explanation.map((x) => (
                      <li key={x.factor + x.detail} className="flex items-center gap-2 text-[12px]">
                        <span className="w-16 shrink-0 text-muted">{titleCase(x.factor)}</span>
                        <span className="h-1.5 w-16 shrink-0 overflow-hidden rounded-full bg-sunken">
                          <span className="block h-full rounded-full bg-brand" style={{ width: `${Math.min(100, (x.points / 45) * 100)}%` }} />
                        </span>
                        <span className="num w-7 shrink-0 text-right font-medium text-good">+{x.points}</span>
                        <span className="min-w-0 truncate text-ink-2" title={x.detail}>
                          {x.detail}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {can('ACCOUNTANT') && i > 0 && (
                    <div className="mt-2.5 text-right">
                      <Button size="sm" loading={match.isPending && match.variables === s} onClick={() => match.mutate(s)}>
                        Match this instead
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {can('ACCOUNTANT') && (
            <div className="mt-2 text-right">
              <button onClick={() => exclude.mutate()} className="text-[12px] text-muted hover:text-bad">
                Exclude (not a real cash movement)
              </button>
            </div>
          )}
        </div>
      )}
      {categorize && <CategorizeModal t={t} currency={cur} onClose={() => setCategorize(false)} />}
    </section>
  );
}

function ScorePill({ score }: { score: number }) {
  const tone = score >= 90 ? 'bg-good-soft text-good ring-good/25' : score >= 70 ? 'bg-brand-soft text-brand ring-brand/20' : score >= 50 ? 'bg-warn-soft text-warn ring-warn/25' : 'bg-sunken text-muted ring-line-strong';
  return (
    <span className={cx('num inline-flex h-6 min-w-11 items-center justify-center rounded-full px-2 text-[12px] font-semibold ring-1 ring-inset', tone)} title="Match confidence (0–100)">
      {score}
    </span>
  );
}

function CategorizeModal({ t, currency, onClose }: { t: BankTransaction; currency: string; onClose: () => void }) {
  const [accountId, setAccountId] = useState('');
  const [description, setDescription] = useState(t.counterparty || t.description);
  const [department, setDepartment] = useState('');
  const [learn, setLearn] = useState(true);
  const go = useAction(() => api(`/api/bank-transactions/${t.id}/match`, { body: { targetType: 'ACCOUNT', targetId: accountId, description, department: department || undefined, learn } }), {
    success: learn ? 'Categorized; a rule will match similar transactions next time' : 'Categorized and posted',
  });
  return (
    <Modal
      open
      onClose={onClose}
      title="Categorize transaction"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!accountId} loading={go.isPending} onClick={() => go.mutate(undefined, { onSuccess: onClose })}>
            Post
          </Button>
        </>
      }
    >
      <div className="mb-3 rounded-md bg-paper px-3 py-2 text-[12.5px]">
        <div className="flex justify-between">
          <span className="truncate">{t.description}</span>
          <span className="num font-semibold">{money(t.amount, currency)}</span>
        </div>
        <div className="text-muted">{date(t.date)}</div>
      </div>
      <div className="space-y-3">
        <Field label={t.amount < 0 ? 'Expense or balance sheet account' : 'Income or balance sheet account'} hint={`Posts ${t.amount < 0 ? 'Dr account / Cr cash' : 'Dr cash / Cr account'}`}>
          <AccountSelect value={accountId} onChange={setAccountId} />
        </Field>
        <Field label="Description">
          <Input value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <Field label="Department">
          <Input value={department} onChange={(e) => setDepartment(e.target.value)} placeholder="Optional" />
        </Field>
        <div className="rounded-md border border-line p-3">
          <Toggle checked={learn} onChange={setLearn} label="Learn a rule" description="Similar descriptions get this account suggested (and auto-matched) from now on" />
        </div>
      </div>
      {go.error && <ErrorNote error={go.error} className="mt-3" />}
    </Modal>
  );
}

// --- All transactions ---------------------------------------------------------------------------------------------

function TransactionsTab() {
  const banks = useBankAccounts();
  const { can } = useSession();
  const [bankId, setBankId] = useState('');
  const [status, setStatus] = useState('');
  const q = useApi<BankTransaction[]>(`/api/bank-transactions${qs({ bankAccountId: bankId, status, limit: 300 })}`);
  const unmatch = useAction((id: string) => api(`/api/bank-transactions/${id}/unmatch`, { method: 'POST' }), { success: 'Match undone; the posting was reversed' });
  return (
    <Card pad={false}>
      <div className="flex flex-wrap gap-2 border-b border-line px-4 py-3">
        <Select className="w-56" value={bankId} onChange={(e) => setBankId(e.target.value)}>
          <option value="">All bank accounts</option>
          {banks.list.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </Select>
        <Select className="w-40" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Any status</option>
          {['UNMATCHED', 'MATCHED', 'EXCLUDED'].map((s) => (
            <option key={s} value={s}>
              {titleCase(s)}
            </option>
          ))}
        </Select>
      </div>
      <Query q={q}>
        {(rows) => (
          <Table dense>
            <thead>
              <tr>
                <Th w="110px">Date</Th>
                <Th>Description</Th>
                <Th>Account</Th>
                <Th right>Amount</Th>
                <Th>Status</Th>
                <Th w="90px" />
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => {
                const b = banks.byId.get(t.bankAccountId);
                return (
                  <Tr key={t.id}>
                    <Td className="num">{date(t.date)}</Td>
                    <Td className="max-w-[420px]">
                      <div className="truncate">{t.description}</div>
                      {t.counterparty && <div className="truncate text-[11.5px] text-muted">{t.counterparty}</div>}
                    </Td>
                    <Td muted>{b?.name}</Td>
                    <Td right className={t.amount > 0 ? 'text-good' : ''}>
                      {money(t.amount, b?.currency, { sign: true })}
                    </Td>
                    <Td>
                      <StatusBadge status={t.status} />
                    </Td>
                    <Td>
                      {t.status === 'MATCHED' && can('ACCOUNTANT') && (
                        <button className="text-[12px] text-muted hover:text-bad" onClick={() => unmatch.mutate(t.id)}>
                          Unmatch
                        </button>
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Query>
    </Card>
  );
}

// --- Bank accounts ------------------------------------------------------------------------------------------------

function AccountsTab() {
  const banks = useBankAccounts();
  const accounts = useAccounts();
  const { can, currency } = useSession();
  const [params, setParams] = useSearchParams();
  const [importing, setImporting] = useState<BankAccount | null>(null);
  const [editing, setEditing] = useState<BankAccount | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [adding, setAdding] = useState(() => params.get('add') === '1' && can('ADMIN'));
  const closeAdd = () => {
    setAdding(false);
    if (params.has('add')) setParams({ tab: 'accounts' }, { replace: true });
  };
  const position = useApi<{ accounts: { accountId: string; balance: number }[] }>('/api/reports/cash-position');
  const gl = new Map((position.data?.accounts ?? []).map((a) => [a.accountId, a.balance]));
  const add = can('ADMIN') && (
    <Button variant="primary" icon={<Icons.Plus size={15} />} onClick={() => setAdding(true)}>
      Add bank account
    </Button>
  );
  return (
    <>
      {banks.list.length > 0 && (add || banks.list.length > banks.active.length) && (
        <div className="mb-3 flex items-center justify-end gap-3">
          <ShowArchived count={banks.list.length - banks.active.length} value={showArchived} onChange={setShowArchived} />
          {add}
        </div>
      )}
      {!banks.isLoading && banks.list.length === 0 && (
        <Card>
          <Empty title="No bank accounts yet" icon={<Icons.Bank size={26} />} action={add}>
            {can('ADMIN')
              ? 'Add each bank account the company uses and tie it to its cash account in the ledger. Then import a statement, and every line gets a suggested match.'
              : 'An owner or admin adds the bank accounts. Then statements can be imported and matched here.'}
          </Empty>
        </Card>
      )}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {(showArchived ? banks.list : banks.active).map((b) => {
          const a = accounts.byId.get(b.glAccountId);
          return (
            <Card
              key={b.id}
              title={
                <>
                  {b.name}
                  <ArchivedBadge at={b.archivedAt} />
                </>
              }
              subtitle={`${b.institution ?? 'Bank'} ··${b.mask ?? '0000'} · ${b.currency}`}
              actions={
                can('ACCOUNTANT') &&
                !b.archivedAt && (
                  <Button size="sm" icon={<Icons.Upload size={14} />} onClick={() => setImporting(b)}>
                    Import CSV
                  </Button>
                )
              }
              footer={
                can('ADMIN') && (
                  <div className="-my-1 flex justify-end">
                    <Button size="sm" variant="ghost" onClick={() => setEditing(b)}>
                      Edit details
                    </Button>
                  </div>
                )
              }
            >
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[11.5px] text-muted">Bank feed balance</div>
                  <div className="num text-[17px] font-semibold">{money(b.feedBalance, b.currency)}</div>
                </div>
                <div>
                  <div className="text-[11.5px] text-muted">Ledger account</div>
                  <Link to={`/accounts/${b.glAccountId}`} className="text-[13px] text-brand hover:underline">
                    {a ? `${a.code} · ${a.name}` : 'View'}
                  </Link>
                  {gl.has(b.glAccountId) && b.currency === currency && <div className="num text-[12px] text-muted">{money(gl.get(b.glAccountId), currency)} in GL</div>}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
      {importing && <ImportModal bank={importing} onClose={() => setImporting(null)} />}
      {editing && <EditBankAccountModal bank={editing} onClose={() => setEditing(null)} />}
      {adding && <AddBankAccountModal currency={params.get('currency') ?? undefined} onClose={closeAdd} />}
    </>
  );
}

/** The name and details people see. Currency and ledger account stay fixed: reconciliations depend on them. */
function EditBankAccountModal({ bank: b, onClose }: { bank: BankAccount; onClose: () => void }) {
  const [f, setF] = useState({ name: b.name, institution: b.institution ?? '', mask: b.mask ?? '' });
  const save = useAction(() => api(`/api/bank-accounts/${b.id}`, { method: 'PATCH', body: { name: f.name.trim(), institution: f.institution.trim() || null, mask: f.mask.trim() || null } }), { success: 'Bank account saved' });
  return (
    <Modal
      open
      onClose={onClose}
      title="Edit bank account"
      footer={
        <>
          <span className="mr-auto">
            <ArchiveButton path={`/api/bank-accounts/${b.id}`} archived={!!b.archivedAt} noun="Bank account" onDone={onClose} />
          </span>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!f.name.trim()} loading={save.isPending} onClick={() => save.mutate(undefined, { onSuccess: onClose })}>
            Save
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label="Name" className="col-span-2">
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        </Field>
        <Field label="Bank">
          <Input value={f.institution} onChange={(e) => setF({ ...f, institution: e.target.value })} placeholder="e.g. Chase" />
        </Field>
        <Field label="Last 4 digits">
          <Input inputMode="numeric" maxLength={8} value={f.mask} onChange={(e) => setF({ ...f, mask: e.target.value })} />
        </Field>
        <p className="col-span-2 text-[12px] text-muted">Currency ({b.currency}) and the ledger account stay fixed: matches and reconciliations depend on them. Archive an account you closed; its history stays.</p>
      </div>
      {save.error && <ErrorNote error={save.error} className="mt-3" />}
    </Modal>
  );
}

const BANK_CURRENCIES = ['USD', 'EUR', 'GBP', 'INR', 'CAD', 'AUD', 'SGD', 'JPY'];

function AddBankAccountModal({ currency, onClose }: { currency?: string; onClose: () => void }) {
  const accounts = useAccounts();
  const banks = useBankAccounts();
  const subs = useSubsidiaries();
  const linked = new Set(banks.list.map((b) => b.glAccountId));
  // Cash accounts in the ledger, the ones not yet tied to a bank first.
  const cash = accounts.accounts.filter((a) => a.isActive && (a.subtype === 'BANK' || a.subtype === 'CREDIT_CARD')).sort((a, b) => Number(linked.has(a.id)) - Number(linked.has(b.id)) || a.code.localeCompare(b.code));
  const [f, setF] = useState({ name: '', institution: '', mask: '', glAccountId: '', subsidiaryId: '', currency: currency ?? '' });
  const gl = f.glAccountId || cash[0]?.id || '';
  const sub = subs.list.find((s) => s.id === f.subsidiaryId) ?? subs.list[0];
  const cur = f.currency || sub?.currency || 'USD';
  const create = useAction(
    () => api<BankAccount>('/api/bank-accounts', { body: { name: f.name.trim(), institution: f.institution.trim() || undefined, mask: f.mask.trim() || undefined, currency: cur, glAccountId: gl, subsidiaryId: sub?.id } }),
    { success: (b) => `${b.name} added. Import a statement next.` },
  );
  return (
    <Modal
      open
      onClose={onClose}
      title="Add a bank account"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!f.name.trim() || !gl} loading={create.isPending} onClick={() => create.mutate(undefined, { onSuccess: onClose })}>
            Add bank account
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label="Name" className="col-span-2">
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Operating account" />
        </Field>
        <Field label="Bank" hint="Optional">
          <Input value={f.institution} onChange={(e) => setF({ ...f, institution: e.target.value })} placeholder="Chase" />
        </Field>
        <Field label="Last 4 digits" hint="Optional">
          <Input value={f.mask} inputMode="numeric" onChange={(e) => setF({ ...f, mask: e.target.value.replace(/\D/g, '').slice(-4) })} placeholder="1234" />
        </Field>
        <Field label="Ledger account" className="col-span-2" hint="Payments and matched bank lines post to this account.">
          <Select value={gl} onChange={(e) => setF({ ...f, glAccountId: e.target.value })}>
            {cash.map((a) => (
              <option key={a.id} value={a.id}>
                {a.code} · {a.name}
                {linked.has(a.id) ? ' (already linked)' : ''}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Currency" className={subs.list.length > 1 ? undefined : 'col-span-2'} hint={sub && cur !== sub.currency ? `Converted to ${sub.currency} in the ledger` : undefined}>
          <Select value={cur} onChange={(e) => setF({ ...f, currency: e.target.value })}>
            {(BANK_CURRENCIES.includes(cur) ? BANK_CURRENCIES : [cur, ...BANK_CURRENCIES]).map((c) => (
              <option key={c}>{c}</option>
            ))}
          </Select>
        </Field>
        {subs.list.length > 1 && (
          <Field label="Entity">
            <Select value={sub?.id ?? ''} onChange={(e) => setF({ ...f, subsidiaryId: e.target.value })}>
              {subs.list.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.currency})
                </option>
              ))}
            </Select>
          </Field>
        )}
      </div>
    </Modal>
  );
}

/** Shown in a payment form when there is nowhere to deposit or pay from yet. */
export function NeedBankAccount({ currency }: { currency: string }) {
  const { can } = useSession();
  return (
    <div className="rounded-[10px] border border-warn/20 bg-warn-soft px-3 py-2 text-[12.5px] text-warn">
      There is no {currency} bank account yet.{' '}
      {can('ADMIN') ? (
        <Link to={`/cash?tab=accounts&add=1&currency=${currency}`} className="font-medium underline underline-offset-2">
          Add a bank account
        </Link>
      ) : (
        'Ask an owner or admin to add one in Bank reconciliation.'
      )}
    </div>
  );
}

function ImportModal({ bank, onClose }: { bank: BankAccount; onClose: () => void }) {
  const [csv, setCsv] = useState('Date,Description,Amount\n');
  const file = useRef<HTMLInputElement>(null);
  const go = useAction(() => api<ImportResult>(`/api/bank-accounts/${bank.id}/import`, { body: { csv } }), { success: (r) => `Imported ${r.imported} transactions${r.skipped ? `, skipped ${r.skipped} duplicates` : ''}` });
  return (
    <Modal
      open
      onClose={onClose}
      width="max-w-2xl"
      title={`Import transactions into ${bank.name}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={go.isPending} disabled={csv.trim().split('\n').length < 2} onClick={() => go.mutate(undefined, { onSuccess: onClose })}>
            Import
          </Button>
        </>
      }
    >
      <p className="mb-3 text-[12.5px] text-muted">
        CSV with <b>Date</b>, <b>Description</b> and <b>Amount</b> (or <b>Debit</b>/<b>Credit</b>) columns, in {bank.currency}. Re-importing the same rows is safe: duplicates are skipped.
      </p>
      <input
        ref={file}
        type="file"
        accept=".csv,text/csv"
        className="mb-3 block text-[12.5px]"
        onChange={async (e) => {
          const f = e.target.files?.[0];
          if (f) setCsv(await f.text());
        }}
      />
      <Textarea className="h-48 font-mono text-[12px]" value={csv} onChange={(e) => setCsv(e.target.value)} />
      {go.error && <ErrorNote error={go.error} className="mt-3" />}
    </Modal>
  );
}

// --- Rules --------------------------------------------------------------------------------------------------------

function RulesTab() {
  const q = useApi<Rule[]>('/api/rules');
  const accounts = useAccounts();
  const { can } = useSession();
  const [creating, setCreating] = useState(false);
  const toggle = useAction((r: Rule) => api(`/api/rules/${r.id}`, { method: 'PATCH', body: { active: !r.active } }), { success: 'Rule updated' });
  return (
    <Card
      pad={false}
      title="Reconciliation rules"
      subtitle="Rules match recurring transactions (payroll, fees, subscriptions) straight to an account"
      actions={
        can('ACCOUNTANT') && (
          <Button size="sm" variant="primary" icon={<Icons.Plus size={14} />} onClick={() => setCreating(true)}>
            New rule
          </Button>
        )
      }
    >
      <Query q={q}>
        {(rows) => (
          <Table>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>When description contains</Th>
                <Th>Direction</Th>
                <Th>Post to</Th>
                <Th right>Hits</Th>
                <Th>Origin</Th>
                <Th>Active</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const a = accounts.byId.get(r.accountId);
                return (
                  <tr key={r.id}>
                    <Td className="font-medium">{r.name}</Td>
                    <Td>
                      <code className="rounded bg-sunken px-1.5 py-0.5 font-mono text-[12px]">{r.pattern}</code>
                    </Td>
                    <Td muted>{r.direction === 'IN' ? 'Money in' : r.direction === 'OUT' ? 'Money out' : 'Either'}</Td>
                    <Td muted>{a ? `${a.code} · ${a.name}` : '—'}</Td>
                    <Td right>{r.hitCount}</Td>
                    <Td>{r.learned ? <Badge tone="ai">Learned</Badge> : <Badge>Manual</Badge>}</Td>
                    <Td>
                      <button disabled={!can('ACCOUNTANT')} onClick={() => toggle.mutate(r)} className={cx('relative h-5 w-9 rounded-full transition-colors', r.active ? 'bg-brand' : 'bg-line-strong')} role="switch" aria-checked={r.active} aria-label="Active">
                        <span className={cx('absolute top-0.5 left-0.5 size-4 rounded-full bg-white shadow transition-transform', r.active && 'translate-x-4')} />
                      </button>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Query>
      {creating && <NewRuleModal onClose={() => setCreating(false)} />}
    </Card>
  );
}

function NewRuleModal({ onClose }: { onClose: () => void }) {
  const [f, setF] = useState({ name: '', pattern: '', direction: 'OUT', accountId: '' });
  const go = useAction(() => api('/api/rules', { body: f }), { success: 'Rule created' });
  return (
    <Modal
      open
      onClose={onClose}
      title="New rule"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!f.name || f.pattern.length < 2 || !f.accountId} loading={go.isPending} onClick={() => go.mutate(undefined, { onSuccess: onClose })}>
            Create rule
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Name">
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Stripe fees" />
        </Field>
        <Field label="Description contains" hint="Case-insensitive">
          <Input value={f.pattern} onChange={(e) => setF({ ...f, pattern: e.target.value })} placeholder="STRIPE FEE" />
        </Field>
        <Field label="Direction">
          <Select value={f.direction} onChange={(e) => setF({ ...f, direction: e.target.value })}>
            <option value="OUT">Money out</option>
            <option value="IN">Money in</option>
            <option value="ANY">Either</option>
          </Select>
        </Field>
        <Field label="Post to account">
          <AccountSelect value={f.accountId} onChange={(v) => setF({ ...f, accountId: v })} />
        </Field>
      </div>
      {go.error && <ErrorNote error={go.error} className="mt-3" />}
    </Modal>
  );
}
